import { useMemo } from "react";
import { CoreIconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  SummaryLineType,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { RentalRate, Vehicle } from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Icon,
  PriceBreakdown,
  PriceDisplay,
  Tooltip,
  usePriceDisplay,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarPriceBreakdown.module.scss";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";
import { getVehicleClassLabelFromVehicle } from "~core-cars/utilities";

export type CarPriceBreakdownType = {
  breakdown: Omit<RentalRate, "extensions">;
  vehicle: Vehicle;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.CarPriceBreakdown;

export const CarPriceBreakdown = (props: CarPriceBreakdownType) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { className, children, vehicle, breakdown } = props;

  const { t, formatFiatCurrency } = useI18nContext();

  const hasPayAtDesk = useMemo(
    () => breakdown.payAtDesk.value > 0,
    [breakdown.payAtDesk.value]
  );

  const formatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  const total = usePriceDisplay({
    prices: breakdown.totalCharge,
    formatOptions,
  });

  const breakdownLineItems = {
    priceLineItems: [
      {
        lineTitle: getVehicleClassLabelFromVehicle(t, vehicle),
        baseAmount: {
          label: t("core-cars.priceBreakdown.baseFare"),
          value: formatFiatCurrency(breakdown.base),
        },
        taxesAndFees: {
          label: t("core-cars.priceBreakdown.taxesAndFees"),
          value: formatFiatCurrency(breakdown.feesSummary.total),
        },
      },
    ],
    summaryLineItems: [
      {
        label: t("core-cars.priceBreakdown.total"),
        value: hasPayAtDesk ? (
          total.priceText
        ) : (
          <PriceDisplay
            prices={breakdown.totalCharge}
            formatOptions={formatOptions}
          />
        ),
        type: SummaryLineType.TOTAL,
      },
      ...(hasPayAtDesk
        ? [
            {
              label: t("core-cars.priceBreakdown.payNow"),
              value: formatFiatCurrency(breakdown.payNow.fiat),
              type: SummaryLineType.SUB_TOTAL,
            },
            {
              label: (
                <>
                  {t("core-cars.priceBreakdown.payAtDesk")}{" "}
                  <Tooltip
                    content={t("core-cars.priceBreakdown.payAtDeskInfo")}
                  >
                    <Icon iconName={CoreIconNameEnum.info} />
                  </Tooltip>
                </>
              ),
              value: formatFiatCurrency(breakdown.payAtDesk),
              type: SummaryLineType.SUB_TOTAL,
            },
            {
              label: t("core-cars.priceBreakdown.payNow"),
              value: (
                <PriceDisplay
                  prices={breakdown.payNow}
                  formatOptions={formatOptions}
                />
              ),
              type: SummaryLineType.TOTAL,
            },
          ]
        : []),
    ],
  };

  return (
    <PriceBreakdown className={clsx(block, className)} {...breakdownLineItems}>
      {children}
    </PriceBreakdown>
  );
};
